/**
 * The login round trip. A guard that turns someone away stashes where they were heading in
 * `?returnUrl=`, and the login page sends them there once they are in — so an inbound deep link
 * (a HubSpot cart link, say) survives an expired session instead of dumping them on the dashboard.
 */

/** Query param the original destination travels in. */
export const RETURN_URL_PARAM = 'returnUrl';

/** Where login lands when there is nothing to return to. */
export const DEFAULT_LANDING_URL = '/dashboard';

/**
 * Pages that are part of getting logged in. Returning to one of these would either bounce straight
 * back out or loop, so they are never treated as a destination.
 */
const AUTH_PATHS = new Set([
  '/login',
  '/logout',
  '/forgot-password',
  '/reset-password',
  '/force-reset-password',
]);

/**
 * Narrows a `returnUrl` to something safe to navigate to, or `null` when there is nothing worth
 * returning to. The value reaches us from the address bar, so it is treated as untrusted input:
 * only an app-internal path is allowed through, never a URL that could carry someone off-site.
 */
export function safeReturnUrl(value: string | null | undefined): string | null {
  const url = value?.trim() ?? '';

  // Must be a rooted path. `//host` is protocol-relative, and browsers normalise the backslash
  // forms to the same thing — all of them would navigate away from the app.
  if (!url.startsWith('/') || url.startsWith('//') || url.startsWith('/\\')) return null;

  // The root redirects to the landing page on its own, so returning to either is a no-op.
  const path = url.split(/[?#]/)[0]!;
  if (path === '/' || path === DEFAULT_LANDING_URL || AUTH_PATHS.has(path)) return null;

  return url;
}

/**
 * The `queryParams` to hand `Router.createUrlTree` / `Router.navigate` when redirecting someone to
 * log in. Empty when the destination is not worth carrying, so the login URL stays clean.
 */
export function returnUrlExtras(destination: string): { queryParams?: Record<string, string> } {
  const url = safeReturnUrl(destination);
  return url === null ? {} : { queryParams: { [RETURN_URL_PARAM]: url } };
}
